import React, { useState } from 'react';
import { AlertTriangle, Calendar, CircleAlert, Download, Eye, FileText, Globe2, Image as ImageIcon, MinusCircle, Package, Pencil, Plus, Settings, X } from 'lucide-react';
import { ReadinessRing } from '../ui/ReadinessRing';
import { PhFindingSection } from './PhFindingSection';
import { phLabelSections } from '../../data/phLabelFindings';
type ReportTab = 'overview' | 'findings' | 'product-data' | 'documents';
const reportTabs: {
  id: ReportTab;
  label: string;
  badge?: string;
}[] = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'findings',
  label: 'Findings',
  badge: '13'
}, {
  id: 'product-data',
  label: 'Product data'
}, {
  id: 'documents',
  label: 'Documents'
}];
const artwork = 'https://cdn.magicpatterns.com/uploads/uzEQeWptNTxSnh4umf4wK4/Capture_d’ecran_2026-08-18_a_12.12.45.png';
const topIssues = [{
  title: 'HALAL certification',
  detail: 'Generic seal, no accredited certifying body named.'
}, {
  title: '27% MORE PROTEIN',
  detail: 'Fails the absolute-difference test despite clearing the relative-percentage bar.'
}, {
  title: '% PDRI declaration',
  detail: "Nutrition panel doesn't show %PDRI values anywhere."
}, {
  title: 'Mandatory nutrient presence',
  detail: 'Trans Fat, Cholesterol, and Dietary Fibre are missing.'
}, {
  title: 'Nutrition Information statement',
  detail: "'Based on PDRI 2015' is missing from the panel."
}];
const sectionJumps: {
  label: string;
  count: number;
  review?: boolean;
}[] = [{
  label: 'Nutrition Panel',
  count: 11
}, {
  label: 'Mandatory Elements',
  count: 2
}, {
  label: 'Claims',
  count: 3
}, {
  label: 'Composition',
  count: 0
}, {
  label: 'Allergens',
  count: 0
}, {
  label: 'Ingredient Permissibility',
  count: 0
}, {
  label: 'Additives',
  count: 0
}, {
  label: 'Illustrations',
  count: 0
}, {
  label: 'Visual & Layout Review',
  count: 3,
  review: true
}];
export function PhLabelTab() {
  const [tab, setTab] = useState<ReportTab>('overview');
  const [showArtwork, setShowArtwork] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  return <div className="space-y-5" data-id="element-1911" data-mp="18m3urheyjxg4">
      <div data-id="element-1912" data-mp="18lbx811svpn7">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b968d]" data-id="element-1913" data-mp="18l29kk489uko">Label score by market</p>
        <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-1914" data-mp="1ddltrzxf7nyg">
          <span className="inline-flex items-center gap-2 rounded-full border border-forest-green bg-[#f2fbf4] px-3 py-1.5 font-mono text-sm font-semibold text-evergreen" data-id="element-1915" data-mp="1iavjlgoq21mt">
            <span className="text-base" aria-hidden="true" data-id="element-1916" data-mp="pmzr6o66qvvu">
              🇵🇭
            </span>
            PH
            <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" data-id="element-1917" data-mp="um2tw1yp0q0n" />
            <span className="text-forest-green" data-id="element-1918" data-mp="zl5lokpnwzqo">69</span>
          </span>
          <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#c3cfc3] px-3 py-1.5 text-xs font-semibold text-[#7d8c80] transition hover:border-[#9fae9f]" data-id="element-1919" data-mp="zj2pfixx2adi">
            <Plus className="h-3.5 w-3.5" data-id="element-1920" data-mp="3vhld3iiellti" />
            Add
          </button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-[#dbe4d8] bg-[#f5f8f3] px-4 py-3 text-sm leading-relaxed text-[#5d6f62]" data-id="element-1921" data-mp="3qkl8zd546t51">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#718174]" data-id="element-1922" data-mp="3lqgzam4gs709" />
        Reviewed against the v5 proof artwork; each finding linked to its primary regulatory source.
      </div>

      <section className="overflow-hidden rounded-2xl border border-amber-200 bg-white shadow-sm" aria-label="Label verdict" data-id="element-1923" data-mp="3bvyu1cjeg3ec">
        <div className="flex flex-col gap-4 border-b border-amber-100 bg-amber-50 p-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-1924" data-mp="32txyql7q7rfb">
          <div className="flex items-start gap-3" data-id="element-1925" data-mp="2xvwf9z6e1q3b">
            <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-600" data-id="element-1926" data-mp="2xx7ju17nr1vh" />
            <div data-id="element-1927" data-mp="111t8de5vl26p">
              <p className="font-[Figtree] text-xl font-semibold leading-tight text-amber-950" data-id="element-1928" data-mp="1108dfnfnfh8b">Needs work</p>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-amber-900" data-id="element-1929" data-mp="3pvhmxidrj24h">
                13 failures, 2 warnings and 3 checks needing manual review — most trace back to one missing nutrition-panel element. Two claims need
                attention before this label can ship.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:justify-end" data-id="element-1930" data-mp="3kvbs5tc74zi6">
            <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-1931" data-mp="366ytuppmz4ki">
              <Download className="h-4 w-4" data-id="element-1932" data-mp="2x7c26vmpld4n" />
              Download PDF
            </button>
            <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-evergreen px-3 py-2 text-sm font-semibold text-white transition hover:bg-forest-green" data-id="element-1933" data-mp="325nah1xcvfkr">
              Scan another →
            </button>
            <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-1934" data-mp="2s6o1tjclbjxr">
              <Pencil className="h-4 w-4" data-id="element-1935" data-mp="24ldofzntafwx" />
              Review report
            </button>
            <button type="button" aria-label="Report settings" className="rounded-lg border border-[#d6dfd3] bg-white p-2 text-[#637667] transition hover:bg-[#f7faf5]" data-id="element-1936" data-mp="2il4xrn3dv84l">
              <Settings className="h-4 w-4" data-id="element-1937" data-mp="2rkjngkbq7eo1" />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-center" data-id="element-1938" data-mp="2nj6fo4p2p1eh">
          <div className="flex shrink-0 flex-col items-center gap-1" data-id="element-1939" data-mp="2nh3rzrbe7kcx">
            <ReadinessRing score={69} label="Label compliance score" data-id="element-1940" data-mp="3199zkp0hcj0r" />
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b968d]" data-id="element-1941" data-mp="2wb8ddv7rc3l2">Score</p>
          </div>
          <img src={artwork} alt="Cheese Snack label artwork" className="h-24 w-24 shrink-0 rounded-xl border border-[#e1e8df] object-cover" data-id="element-1942" data-mp="3b9aii9ufsx4f" />
          <div className="min-w-0" data-id="element-1943" data-mp="3de75cmvymaqn">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b968d]" data-id="element-1944" data-mp="3db1g97nbz8ux">Product</p>
            <h2 className="mt-1 font-[Figtree] text-2xl font-semibold tracking-tight text-evergreen" data-id="element-1945" data-mp="3nb20cvt0xt06">Cheese Snack</h2>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-1946" data-mp="3s2af87d4i6kq">
              <Pill data-id="element-1947" data-mp="3s3v9dvvizni2">DairyWorks</Pill>
              <Pill icon={<Package className="h-3.5 w-3.5" data-id="element-1949" data-mp="w8vzbj086o0" />} data-id="element-1948" data-mp="w0jvluph5dv">Snack · Cheese &amp; Crackers</Pill>
              <Pill icon={<Globe2 className="h-3.5 w-3.5" data-id="element-1951" data-mp="4wb0dsg4je6z" />} data-id="element-1950" data-mp="4wje1mk2mnj8">Philippines</Pill>
              <Pill icon={<Calendar className="h-3.5 w-3.5" data-id="element-1953" data-mp="9wf9uyim78np" />} data-id="element-1952" data-mp="9w5qvuvzm1ri">17 Aug 2026</Pill>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 divide-[#e5ebe3] border-t border-[#e5ebe3] lg:grid-cols-5 lg:divide-x" data-id="element-1954" data-mp="evidqfmuz8u3">
          <Metric icon={<AlertTriangle className="h-4 w-4 text-red-600" data-id="element-1956" data-mp="2bp3dh8z67y40" />} value="13" caption="Failures" tone="text-red-700" data-id="element-1955" data-mp="2bok4ihri0k24" />
          <Metric icon={<AlertTriangle className="h-4 w-4 text-amber-600" data-id="element-1958" data-mp="2gogcst78axnh" />} value="3" caption="Warnings" tone="text-amber-700" data-id="element-1957" data-mp="2gmvh3096h8o1" />
          <Metric icon={<Eye className="h-4 w-4 text-slate-500" data-id="element-1960" data-mp="24bufyt5s4y79" />} value="3" caption="Needs review" tone="text-slate-600" data-id="element-1959" data-mp="24cdv6241p8hl" />
          <Metric icon={<CircleAlert className="h-4 w-4 text-[#8b968d]" data-id="element-1962" data-mp="1zecc3h6md964" />} value="1" caption="Inconclusive" tone="text-[#5d6f62]" data-id="element-1961" data-mp="1zchv2dygy17b" />
          <Metric icon={<MinusCircle className="h-4 w-4 text-[#adb8ae]" data-id="element-1964" data-mp="1uc3kao5hgd5l" />} value="4" caption="Skipped" tone="text-[#8b968d]" data-id="element-1963" data-mp="1ufrbhgnggnh5" />
        </div>
      </section>

      <div className="border-b border-[#dce3da]" data-id="element-1965" data-mp="2n2c7iuevlnr1">
        <nav className="flex flex-wrap gap-1" aria-label="Label report sections" data-id="element-1966" data-mp="2n3nawt4esthm">
          {reportTabs.map(reportTab => <button key={reportTab.id} type="button" onClick={() => setTab(reportTab.id)} aria-current={tab === reportTab.id ? 'page' : undefined} className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition ${tab === reportTab.id ? 'border-forest-green text-forest-green' : 'border-transparent text-[#718174] hover:text-evergreen'}`} data-id="element-1967" data-mp="2d4pk1jjignvg">
              {reportTab.label}
              {reportTab.badge && <span className="rounded-full bg-red-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-red-800" data-id="element-1968" data-mp="1iekpdh7zb21b">{reportTab.badge}</span>}
            </button>)}
        </nav>
      </div>

      {tab === 'overview' && <div className="grid gap-4 lg:grid-cols-3" data-id="element-1969" data-mp="2f2zccbjslry6">
          <section className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm lg:col-span-2" aria-labelledby="ph-top-issues" data-id="element-1970" data-mp="2f2h7wrmz87f6">
            <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1971" data-mp="25dfpyvwvffyk">
              <h3 id="ph-top-issues" className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1972" data-mp="25b32624fmbdl">
                Top issues
              </h3>
              <button type="button" onClick={() => setTab('findings')} className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-forest-green transition hover:underline" data-id="element-1973" data-mp="2xus5q72rxf6f">
                View all →
              </button>
            </div>
            <ul className="mt-3 divide-y divide-[#edf0eb]" data-id="element-1974" data-mp="w41ibxli96bh">
              {topIssues.map(issue => <li key={issue.title} className="flex flex-wrap items-start gap-3 py-3" data-id="element-1975" data-mp="r69ogy1ava4n">
                  <span className="mt-0.5 shrink-0 rounded-full bg-red-100 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-red-700" data-id="element-1976" data-mp="3gfceiot1plg">
                    Fail
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-1977" data-mp="dc085z3a5tmr">
                    <p className="text-sm font-semibold leading-relaxed text-evergreen" data-id="element-1978" data-mp="8eqlgr79zw2m">{issue.title}</p>
                    <p className="mt-0.5 text-sm text-[#8b968d]" data-id="element-1979" data-mp="1os714et00l8o">{issue.detail}</p>
                  </div>
                </li>)}
            </ul>
          </section>

          <section className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" aria-labelledby="ph-sections" data-id="element-1980" data-mp="24eqsip1qb473">
            <h3 id="ph-sections" className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1981" data-mp="1zg74mj3kric3">
              Sections
            </h3>
            <ul className="mt-3 divide-y divide-[#edf0eb]" data-id="element-1982" data-mp="1umcmvl7j5dfn">
              {sectionJumps.map(row => <li key={row.label} data-id="element-1983" data-mp="1pni7kt8ru70a">
                  <button type="button" onClick={() => setTab('findings')} className={`flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2.5 text-left transition ${row.review ? 'bg-sky-50/70 hover:bg-sky-50' : 'hover:bg-[#f7faf5]'}`} data-id="element-1984" data-mp="1kqi1iibx9496">
                    <span className={`text-sm font-medium ${row.review ? 'text-sky-900' : 'text-evergreen'}`} data-id="element-1985" data-mp="363kqi4acqpir">{row.label}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold ${row.review ? 'bg-sky-100 text-sky-700' : row.count > 0 ? 'bg-red-100 text-red-700' : 'bg-[#f0f3ef] text-[#8b968d]'}`} data-id="element-1986" data-mp="31488bwufe6mi">
                      {row.count}
                    </span>
                  </button>
                </li>)}
            </ul>
          </section>
        </div>}

      {tab === 'findings' && <section aria-label="Findings" data-id="element-1987" data-mp="2uohridzrtida">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1988" data-mp="2znm3fce2xu7b">
            <h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1989" data-mp="34m5prdwl5807">Findings</h2>
            <button type="button" onClick={() => setShowArtwork(open => !open)} aria-expanded={showArtwork} className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5] sm:self-auto" data-id="element-1990" data-mp="ex3lq9hjg31r">
              <ImageIcon className="h-4 w-4" data-id="element-1991" data-mp="2b2m7twvf1tit" />
              {showArtwork ? 'Hide artwork' : 'View label artwork'}
            </button>
          </div>

          {showArtwork ? <div className="mt-4 flex flex-col gap-4 lg:flex-row" data-id="element-1992" data-mp="2kz7d8jbcl6n6">
              <div className="max-h-[640px] overflow-y-auto lg:w-2/5 lg:shrink-0" data-id="element-1993" data-mp="1x9wl1q82ckkn">
                <p className="text-xs text-[#8b968d]" data-id="element-1994" data-mp="1saa6ndgi0ya9">Face 1 of 1 · v5 proof artwork</p>
                <button type="button" onClick={() => setEnlarged(true)} className="group relative mt-2 block w-full overflow-hidden rounded-xl border border-[#e1e8df] bg-[#f6f8f5]" data-id="element-1995" data-mp="260ww0f0ddbto">
                  <img src={artwork} alt="Cheese Snack label artwork" className="aspect-[4/3] w-full object-contain" data-id="element-1996" data-mp="1djayu4nhmdrq" />
                  <span className="absolute bottom-2 right-2 rounded-full bg-evergreen/85 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-white opacity-0 transition group-hover:opacity-100" data-id="element-1997" data-mp="19h68besc5x9b">
                    Click to enlarge
                  </span>
                </button>
              </div>
              <div className="max-h-[640px] flex-1 space-y-3 overflow-y-auto pr-1" data-id="element-1998" data-mp="ufzeq4qixwn9">
                {phLabelSections.map((section, index) => <PhFindingSection key={section.title} section={section} defaultOpen={index === 0} data-id="element-1999" data-mp="1i6cyz92var7a" data-mpi={index} />)}
              </div>
            </div> : <div className="mt-4 space-y-3" data-id="element-2000" data-mp="188ffeyvydil0">
              {phLabelSections.map((section, index) => <PhFindingSection key={section.title} section={section} defaultOpen={index === 0} data-id="element-2001" data-mp="1d5pbuyhreuic" data-mpi={index} />)}
            </div>}

          {enlarged && <div role="dialog" aria-modal="true" aria-label="Label artwork" className="fixed inset-0 z-50 flex items-center justify-center bg-evergreen/70 p-6" onClick={() => setEnlarged(false)} data-id="element-2002" data-mp="1rbnlsj3gd1s">
              <div className="w-full max-w-4xl" onClick={event => event.stopPropagation()} data-id="element-2003" data-mp="d65c4bhtv1t0">
                <div className="flex items-center justify-between pb-3" data-id="element-2004" data-mp="86k8vovmk7ah">
                  <p className="font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white" data-id="element-2005" data-mp="3bn3qt1gui2v">Cheese Snack · v5 proof</p>
                  <button type="button" onClick={() => setEnlarged(false)} className="rounded-lg bg-white/15 p-2 text-white hover:bg-white/25" aria-label="Close artwork preview" data-id="element-2006" data-mp="vyhl3h2zl1c4">
                    <X className="h-4 w-4" data-id="element-2007" data-mp="m2e9lkm8vqci" />
                  </button>
                </div>
                <img src={artwork} alt="Label artwork, enlarged" className="w-full rounded-xl bg-white object-contain" data-id="element-2008" data-mp="3bqx3huhvvb7h" />
              </div>
            </div>}
        </section>}

      {(tab === 'product-data' || tab === 'documents') && <p className="px-1 text-sm italic text-[#9aa79c]" data-id="element-2009" data-mp="1r6tayeue4mu3">
          {tab === 'product-data' ? 'No extracted product data has been published for this label yet.' : 'No artwork documents have been attached to this report yet.'}
        </p>}
    </div>;
}
function Pill({
  children,
  icon
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return <span className="inline-flex items-center gap-1.5 rounded-full border border-[#dce3da] bg-[#f9fbf8] px-2.5 py-1 text-xs font-medium text-[#5d6f62]" data-id="element-2010" data-mp="29xjco08863ld">
      {icon}
      {children}
    </span>;
}
function Metric({
  icon,
  value,
  caption,
  tone
}: {
  icon: React.ReactNode;
  value: string;
  caption: string;
  tone: string;
}) {
  return <div className="flex items-center gap-3 px-6 py-4" data-id="element-2011" data-mp="kkpc8nsxh3fa">
      {icon}
      <div data-id="element-2012" data-mp="phfrj8wwruzn">
        <p className={`font-[Figtree] text-2xl font-semibold leading-none ${tone}`} data-id="element-2013" data-mp="pjabol0cvj2o">{value}</p>
        <p className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b968d]" data-id="element-2014" data-mp="hikumkbc37b0">{caption}</p>
      </div>
    </div>;
}